/**
 * DisciplineSettingsPanel
 *
 * Full settings panel for configuring discipline rules:
 * - Auto-lock settings (revenge guard, tilt protection)
 * - Size discipline settings
 * - Stop protection settings
 * - 2FA unlock settings
 */

import React, { useEffect, useState } from 'react';
import {
  X,
  Settings,
  Clock,
  AlertTriangle,
  Flame,
  TrendingDown,
  Shield,
  Mail,
  ChevronDown,
  ChevronUp,
  Info,
  RotateCcw,
  Snowflake,
} from 'lucide-react';
import {
  useDisciplineStore,
  DisciplineSettings,
  DEFAULT_DISCIPLINE_SETTINGS,
  StopMode,
  SizeRecoveryMode,
} from '../../store/disciplineStore';
import { useToastStore } from '../../store/toastStore';
import {
  RESTRAIN_MAX_MINUTES,
  RESTRAIN_PRESETS,
  clampRestrainMinutes,
  formatRestrainMinutes,
  minutesToHmm,
  parseRestrainDuration,
  restrainLockLabel,
  RESTRAIN_VENUE_KEYS,
  type RestrainVenue,
} from '../../store/restrainStore';
import { RESTRAIN_EXPLANATION } from '../Restrain/RestrainToggle';
import { useRestrainLock } from '../Restrain/useRestrain';
import './DisciplineSettingsPanel.css';

interface SettingsSectionProps {
  title: string;
  icon: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

const SettingsSection: React.FC<SettingsSectionProps> = ({
  title,
  icon,
  defaultOpen = false,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className={`settings-section ${isOpen ? 'open' : ''}`}>
      <button className="settings-section-header" onClick={() => setIsOpen(!isOpen)}>
        <div className="settings-section-title">
          {icon}
          <span>{title}</span>
        </div>
        {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {isOpen && <div className="settings-section-content">{children}</div>}
    </div>
  );
};

interface ToggleProps {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

const Toggle: React.FC<ToggleProps> = ({ label, description, checked, onChange, disabled }) => (
  <div className="settings-toggle">
    <div className="settings-toggle-info">
      <span className="settings-toggle-label">{label}</span>
      {description && <span className="settings-toggle-desc">{description}</span>}
    </div>
    <button
      className={`settings-toggle-switch ${checked ? 'on' : ''}`}
      onClick={() => onChange(!checked)}
      disabled={disabled}
    >
      <div className="settings-toggle-knob" />
    </button>
  </div>
);

interface NumberInputProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
}

const NumberInput: React.FC<NumberInputProps> = ({
  label,
  value,
  onChange,
  min = 0,
  max = 999,
  step = 1,
  unit,
}) => (
  <div className="settings-number">
    <span className="settings-number-label">{label}</span>
    <div className="settings-number-input">
      <button onClick={() => onChange(Math.max(min, value - step))}>-</button>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(Math.min(max, Math.max(min, Number(e.target.value))))}
        min={min}
        max={max}
        step={step}
      />
      <button onClick={() => onChange(Math.min(max, value + step))}>+</button>
      {unit && <span className="settings-number-unit">{unit}</span>}
    </div>
  </div>
);

interface SelectProps {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}

const Select: React.FC<SelectProps> = ({ label, value, options, onChange }) => (
  <div className="settings-select">
    <span className="settings-select-label">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </div>
);

interface RestrainDurationInputProps {
  value: number;
  onChange: (minutes: number) => void;
  /** A running restrain: the server refuses duration changes until it ends. */
  disabled?: boolean;
  /** data-testid prefix ("restrain" for futures, "restrain-perps" for perps). */
  testId?: string;
  /** Spoken venue name for the controls' labels. */
  venueLabel?: string;
}

/**
 * Restrain duration, typed as h:mm (also accepts plain minutes or "2h 30m"),
 * 0..480 minutes, committed on blur/Enter. 0 = until flat.
 */
export const RestrainDurationInput: React.FC<RestrainDurationInputProps> = ({ value, onChange, disabled, testId = 'restrain', venueLabel = '' }) => {
  const [draft, setDraft] = useState(() => minutesToHmm(value));
  useEffect(() => { setDraft(minutesToHmm(value)); }, [value]);

  const commit = (minutes: number) => {
    const clamped = clampRestrainMinutes(minutes);
    setDraft(minutesToHmm(clamped));
    if (clamped !== value) onChange(clamped);
  };
  const commitDraft = () => {
    const parsed = parseRestrainDuration(draft);
    if (parsed === null) setDraft(minutesToHmm(value));
    else commit(parsed);
  };

  return (
    <>
      <div className="restrain-chips" role="group" aria-label={`${venueLabel}Restrain duration presets`}>
        {RESTRAIN_PRESETS.map(p => (
          <button
            key={p.minutes}
            type="button"
            className={`restrain-chip${value === p.minutes ? ' selected' : ''}`}
            aria-pressed={value === p.minutes}
            data-testid={`${testId}-chip-${p.minutes}`}
            disabled={disabled}
            onClick={() => commit(p.minutes)}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="settings-number restrain-duration">
        <span className="settings-number-label">
          Duration <span className="settings-number-hint">(h:mm, max 8:00, 0 = until flat)</span>
        </span>
        <div className="settings-number-input">
          <button type="button" aria-label={`Decrease ${venueLabel.toLowerCase()}restrain duration`} disabled={disabled} onClick={() => commit(value - 5)}>-</button>
          <input
            type="text"
            inputMode="numeric"
            aria-label={`${venueLabel}Restrain duration (h:mm)`}
            data-testid={`${testId}-minutes`}
            disabled={disabled}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitDraft}
            onKeyDown={(e) => { if (e.key === 'Enter') commitDraft(); }}
          />
          <button type="button" aria-label={`Increase ${venueLabel.toLowerCase()}restrain duration`} disabled={disabled} onClick={() => commit(Math.min(RESTRAIN_MAX_MINUTES, value + 5))}>+</button>
          <span className="settings-number-unit" data-testid={`${testId}-minutes-label`}>{formatRestrainMinutes(value)}</span>
        </div>
      </div>
    </>
  );
};

interface RestrainControlsProps {
  enabled: boolean;
  minutes: number;
  onEnabled: (v: boolean) => void;
  onMinutes: (v: number) => void;
  /** Which venue these controls set (each has its own toggle, duration and lock). */
  venue?: RestrainVenue;
}

const VENUE_TITLE: Record<RestrainVenue, string> = { futures: 'Futures', perps: 'Perps (Vest)' };

/**
 * One venue's toggle + duration, with that venue's lock: while a restrain of
 * this venue is running the server refuses turning it off or changing the
 * duration, so both are disabled and the status says until when. The other
 * venue is never locked by it. One-shot: it turns itself off when the last
 * restrained position of this venue is flat.
 */
export const RestrainControls: React.FC<RestrainControlsProps> = ({ enabled, minutes, onEnabled, onMinutes, venue = 'futures' }) => {
  const { lock, now } = useRestrainLock(venue);
  const locked = enabled && lock !== null;
  const testId = venue === 'futures' ? 'restrain' : 'restrain-perps';
  const title = VENUE_TITLE[venue];
  return (
    <div className={`restrain-venue restrain-venue-${venue}`} data-testid={`${testId}-venue`}>
      <Toggle
        label={`${title} Restrain`}
        description={`Lock ${venue === 'futures' ? 'futures' : 'perps'} position size from the moment a position opens. Turns off by itself when the last one is flat.`}
        checked={enabled}
        onChange={onEnabled}
        disabled={locked}
      />
      {enabled && (
        <div className={`restrain-lock-status${locked ? ' locked' : ''}`} data-testid={`${testId}-lock-status`}>
          {locked && lock ? `${title} restrain ${restrainLockLabel(lock, now)} (${lock.symbol})` : 'Armed for next position'}
        </div>
      )}
      <RestrainDurationInput value={minutes} onChange={onMinutes} disabled={locked} testId={testId} venueLabel={`${title} `} />
    </div>
  );
};

const DisciplineSettingsPanel: React.FC = () => {
  const isOpen = useDisciplineStore((s) => s.settingsModalOpen);
  const closeModal = useDisciplineStore((s) => s.closeSettingsModal);
  const settings = useDisciplineStore((s) => s.settings);
  const updateSettings = useDisciplineStore((s) => s.updateSettings);
  const setBaseMaxSize = useDisciplineStore((s) => s.setBaseMaxSize);
  const baseMaxSize = useDisciplineStore((s) => s.state.baseMaxSize);

  const addToast = useToastStore((s) => s.addToast);

  if (!isOpen) return null;

  const handleReset = () => {
    if (window.confirm('Reset all discipline settings to defaults?')) {
      updateSettings(DEFAULT_DISCIPLINE_SETTINGS);
      addToast({ type: 'info', message: 'Settings reset to defaults' });
    }
  };

  const update = <K extends keyof DisciplineSettings>(key: K, value: DisciplineSettings[K]) => {
    updateSettings({ [key]: value });
  };

  return (
    <div className="discipline-settings-overlay" onClick={closeModal}>
      <div className="discipline-settings-panel" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="discipline-settings-header">
          <div className="discipline-settings-title">
            <Settings size={18} />
            <span>Discipline Settings</span>
          </div>
          <div className="discipline-settings-actions">
            <button className="reset-btn" onClick={handleReset} title="Reset to defaults">
              <RotateCcw size={16} />
            </button>
            <button className="close-btn" onClick={closeModal}>
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="discipline-settings-content">
          {/* Auto-Lock Rules */}
          <SettingsSection
            title="Auto-Lock Rules"
            icon={<Clock size={16} />}
            defaultOpen={true}
          >
            <div className="settings-info">
              <Info size={14} />
              <span>Automatically lock trading after losses to prevent revenge trading</span>
            </div>

            <Toggle
              label="Revenge Guard"
              description="Lock trading immediately after any loss"
              checked={settings.revengeGuardEnabled}
              onChange={(v) => update('revengeGuardEnabled', v)}
            />
            {settings.revengeGuardEnabled && (
              <NumberInput
                label="Cooldown duration"
                value={settings.revengeGuardMinutes}
                onChange={(v) => update('revengeGuardMinutes', v)}
                min={1}
                max={60}
                unit="min"
              />
            )}

            <div className="settings-divider" />

            <Toggle
              label="Tilt Protection"
              description="Lock after consecutive losses"
              checked={settings.tiltProtectionEnabled}
              onChange={(v) => update('tiltProtectionEnabled', v)}
            />
            {settings.tiltProtectionEnabled && (
              <>
                <NumberInput
                  label="Trigger after"
                  value={settings.tiltProtectionLosses}
                  onChange={(v) => update('tiltProtectionLosses', v)}
                  min={2}
                  max={10}
                  unit="losses"
                />
                <NumberInput
                  label="Cooldown duration"
                  value={settings.tiltProtectionMinutes}
                  onChange={(v) => update('tiltProtectionMinutes', v)}
                  min={5}
                  max={120}
                  unit="min"
                />
              </>
            )}

            <div className="settings-divider" />

            <NumberInput
              label="Default cool-off time"
              value={settings.coolOffDefaultMinutes}
              onChange={(v) => update('coolOffDefaultMinutes', v)}
              min={5}
              max={120}
              unit="min"
            />
          </SettingsSection>

          {/* Restrain: freeze position size while in a position */}
          <SettingsSection
            title="Restrain"
            icon={<Snowflake size={16} />}
            defaultOpen={settings.restrainFuturesEnabled || settings.restrainPerpsEnabled}
          >
            <div className="settings-info">
              <Info size={14} />
              <span>{RESTRAIN_EXPLANATION} Futures and perps are separate: each has its own switch, duration and lock.</span>
            </div>

            {(['futures', 'perps'] as const).map(venue => {
              const k = RESTRAIN_VENUE_KEYS[venue];
              return (
                <RestrainControls
                  key={venue}
                  venue={venue}
                  enabled={settings[k.enabled]}
                  minutes={settings[k.minutes]}
                  onEnabled={(v) => update(k.enabled, v)}
                  onMinutes={(v) => update(k.minutes, v)}
                />
              );
            })}
          </SettingsSection>

          {/* Size Discipline */}
          <SettingsSection
            title="Size Discipline"
            icon={<TrendingDown size={16} />}
          >
            <div className="settings-info">
              <Info size={14} />
              <span>Prevent revenge sizing by limiting position size after losses</span>
            </div>

            <NumberInput
              label="Base max size"
              value={baseMaxSize}
              onChange={setBaseMaxSize}
              min={1}
              max={100}
              unit="contracts"
            />

            <Toggle
              label="Lock size after loss"
              description="Can't increase size until you win"
              checked={settings.sizeLockAfterLoss}
              onChange={(v) => update('sizeLockAfterLoss', v)}
            />

            <div className="settings-divider" />

            <Toggle
              label="Forced size reduction"
              description="Automatically reduce max size after consecutive losses"
              checked={settings.forcedReductionEnabled}
              onChange={(v) => update('forcedReductionEnabled', v)}
            />
            {settings.forcedReductionEnabled && (
              <>
                <NumberInput
                  label="Reduce after"
                  value={settings.forcedReductionAfterLosses}
                  onChange={(v) => update('forcedReductionAfterLosses', v)}
                  min={1}
                  max={10}
                  unit="losses"
                />
                <NumberInput
                  label="Minimum size"
                  value={settings.minimumSize}
                  onChange={(v) => update('minimumSize', v)}
                  min={1}
                  max={10}
                  unit="contracts"
                />
                <Select
                  label="Recovery mode"
                  value={settings.sizeRecoveryMode}
                  options={[
                    { value: 'gradual', label: 'Gradual (+1 per win)' },
                    { value: 'instant', label: 'Instant (full on win)' },
                  ]}
                  onChange={(v) => update('sizeRecoveryMode', v as SizeRecoveryMode)}
                />
              </>
            )}
          </SettingsSection>

          {/* Stop Protection */}
          <SettingsSection
            title="Stop Protection"
            icon={<Shield size={16} />}
          >
            <div className="settings-info">
              <Info size={14} />
              <span>Prevent widening stops to avoid taking losses</span>
            </div>

            <Select
              label="Stop modification mode"
              value={settings.stopMode}
              options={[
                { value: 'flexible', label: 'Flexible (no restrictions)' },
                { value: 'tighten_only', label: 'Tighten Only (can\'t widen)' },
                { value: 'locked', label: 'Locked (no modifications)' },
              ]}
              onChange={(v) => update('stopMode', v as StopMode)}
            />

            {settings.stopMode === 'flexible' && (
              <>
                <div className="settings-divider" />
                <NumberInput
                  label="Max widens per trade"
                  value={settings.maxWidensPerTrade}
                  onChange={(v) => update('maxWidensPerTrade', v)}
                  min={0}
                  max={10}
                />
                <NumberInput
                  label="Max widen amount"
                  value={settings.maxWidenTicks}
                  onChange={(v) => update('maxWidenTicks', v)}
                  min={1}
                  max={100}
                  unit="ticks"
                />
                <NumberInput
                  label="Daily widen limit"
                  value={settings.dailyWidenLimit}
                  onChange={(v) => update('dailyWidenLimit', v)}
                  min={1}
                  max={50}
                  unit="total"
                />
              </>
            )}
          </SettingsSection>

          {/* 2FA Unlock */}
          <SettingsSection
            title="2FA Unlock"
            icon={<Mail size={16} />}
          >
            <div className="settings-info">
              <Info size={14} />
              <span>Add friction to prevent impulsive unlocking</span>
            </div>

            <Toggle
              label="Require 2FA to unlock"
              description="Email OTP with delay before unlock"
              checked={settings.twoFactorEnabled}
              onChange={(v) => update('twoFactorEnabled', v)}
            />

            {settings.twoFactorEnabled && (
              <>
                <div className="settings-input">
                  <span className="settings-input-label">Email address</span>
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={settings.twoFactorEmail}
                    onChange={(e) => update('twoFactorEmail', e.target.value)}
                  />
                </div>

                <Toggle
                  label="Require reflection"
                  description="Must explain why you want to unlock"
                  checked={settings.reflectionRequired}
                  onChange={(v) => update('reflectionRequired', v)}
                />

                {settings.reflectionRequired && (
                  <NumberInput
                    label="Minimum characters"
                    value={settings.reflectionMinChars}
                    onChange={(v) => update('reflectionMinChars', v)}
                    min={20}
                    max={500}
                    unit="chars"
                  />
                )}

                <div className="settings-divider" />

                <div className="settings-group-label">OTP Delay (seconds)</div>
                <div className="settings-delay-grid">
                  <NumberInput
                    label="Cool-off"
                    value={settings.otpDelaySeconds.cool_off}
                    onChange={(v) =>
                      update('otpDelaySeconds', { ...settings.otpDelaySeconds, cool_off: v })
                    }
                    min={0}
                    max={600}
                    unit="s"
                  />
                  <NumberInput
                    label="After loss"
                    value={settings.otpDelaySeconds.revenge_guard}
                    onChange={(v) =>
                      update('otpDelaySeconds', { ...settings.otpDelaySeconds, revenge_guard: v })
                    }
                    min={0}
                    max={600}
                    unit="s"
                  />
                  <NumberInput
                    label="Tilt"
                    value={settings.otpDelaySeconds.tilt_protection}
                    onChange={(v) =>
                      update('otpDelaySeconds', { ...settings.otpDelaySeconds, tilt_protection: v })
                    }
                    min={0}
                    max={600}
                    unit="s"
                  />
                  <NumberInput
                    label="Done for day"
                    value={settings.otpDelaySeconds.done_for_day}
                    onChange={(v) =>
                      update('otpDelaySeconds', { ...settings.otpDelaySeconds, done_for_day: v })
                    }
                    min={0}
                    max={600}
                    unit="s"
                  />
                </div>
              </>
            )}

            <div className="settings-divider" />

            <Toggle
              label="Allow emergency unlock"
              description="Bypass with logging and 24hr cooldown"
              checked={settings.emergencyUnlockEnabled}
              onChange={(v) => update('emergencyUnlockEnabled', v)}
            />

            {settings.emergencyUnlockEnabled && (
              <NumberInput
                label="Emergency cooldown"
                value={settings.emergencyCooldownHours}
                onChange={(v) => update('emergencyCooldownHours', v)}
                min={1}
                max={72}
                unit="hours"
              />
            )}
          </SettingsSection>
        </div>

        {/* Footer */}
        <div className="discipline-settings-footer">
          <button className="save-btn" onClick={closeModal}>
            Save & Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DisciplineSettingsPanel;
